"use client";

import { useEffect, useSyncExternalStore } from "react";
import {
  type MemoContextKeyInput,
  memoContextKey,
} from "@/app/[lang]/[workspaceId]/_components/layout/memo-node-id";
import type { UserMemoContext } from "@/app/[lang]/[workspaceId]/_components/layout/workspace-side-panel-mode";
import { StorageManager } from "@/lib/storage/local-storage";

const MEMO_CHANGED_EVENT = "amethyst:memo-changed";
const MEMO_HOVERED_EVENT = "amethyst:memo-hovered";
const MEMO_DISPLAY_SETTINGS_EVENT = "amethyst:memo-display-settings-changed";
const MEMO_VISIBLE_DATE_RANGE_EVENT = "amethyst:memo-visible-date-range";
const MEMO_RELATED_CONTEXTS_EVENT = "amethyst:memo-related-contexts";
const MEMO_DISPLAY_SETTINGS_KEY = "amethyst:memo-display-settings:v2";

type MemoChangedDetail = {
  context: MemoContextKeyInput;
  publicationScopes?: readonly MemoPublicationScope[];
};

export type MemoContext = MemoContextKeyInput;
export type MemoPublicationScope = "VIEW" | "PROJECT" | "FEATURE" | "WORKSPACE";

export type MemoDisplaySettings = {
  hidePeriodBars: boolean;
  hideMemos: boolean;
  hideMetricTooltipMemos: boolean;
};

export type MemoVisibleDateRange = {
  startDate: string;
  endDate: string;
};

export type MemoHoverDetail = {
  context: MemoContextKeyInput;
  targetId: string;
  source: "chart" | "list";
  memoIds: string[];
  bucket?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

export const DEFAULT_MEMO_DISPLAY_SETTINGS: MemoDisplaySettings = {
  hidePeriodBars: false,
  hideMemos: false,
  hideMetricTooltipMemos: true,
};
let cachedMemoDisplaySettings: MemoDisplaySettings | null = null;
let cachedMemoDisplaySettingsRaw: string | null | undefined;
const cachedMemoVisibleDateRanges = new Map<string, MemoVisibleDateRange>();
const cachedMemoRelatedContexts = new Map<
  string,
  Map<string, { context: UserMemoContext; count: number }>
>();
const cachedMemoRelatedContextSnapshots = new Map<
  string,
  readonly UserMemoContext[]
>();
const EMPTY_MEMO_RELATED_CONTEXTS: readonly UserMemoContext[] = [];

function isMemoDisplaySettings(value: unknown): value is MemoDisplaySettings {
  if (!value || typeof value !== "object") return false;
  const settings = value as Partial<MemoDisplaySettings>;
  return (
    typeof settings.hidePeriodBars === "boolean" &&
    typeof settings.hideMemos === "boolean" &&
    typeof settings.hideMetricTooltipMemos === "boolean"
  );
}

/**
 * v1 localStorage には metric tooltip 専用設定がないため、既存値を保ったまま新 field だけ既定値で補います。
 */
function memoDisplaySettingsFromStored(
  value: unknown,
): MemoDisplaySettings | null {
  if (!value || typeof value !== "object") return null;
  const settings = value as Partial<MemoDisplaySettings>;
  if (
    typeof settings.hidePeriodBars !== "boolean" ||
    typeof settings.hideMemos !== "boolean"
  ) {
    return null;
  }
  return {
    hidePeriodBars: settings.hidePeriodBars,
    hideMemos: settings.hideMemos,
    hideMetricTooltipMemos:
      typeof settings.hideMetricTooltipMemos === "boolean"
        ? settings.hideMetricTooltipMemos
        : DEFAULT_MEMO_DISPLAY_SETTINGS.hideMetricTooltipMemos,
  };
}

/**
 * User memo の mutation 成功を chart 側へ通知します。
 * side panel と chart は別々の GraphQL query を持つため、urql cache だけに頼らず対象 view を明示します。
 */
export function dispatchMemoChanged(
  context: MemoContextKeyInput,
  options?: {
    publicationScopes?: readonly MemoPublicationScope[];
  },
) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<MemoChangedDetail>(MEMO_CHANGED_EVENT, {
      detail: { context, publicationScopes: options?.publicationScopes },
    }),
  );
}

/**
 * 指定 view の memo 変更だけを購読します。
 * 他 feature / target の side panel 操作で chart を再取得しないよう、context key を境界にします。
 */
export function subscribeMemoChanged(
  context: MemoContextKeyInput,
  onChanged: () => void,
) {
  if (typeof window === "undefined") return () => undefined;

  const handleTimelineChanged = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as Partial<MemoChangedDetail> | null;
    const changedContext = detail?.context;
    if (!changedContext) {
      return;
    }
    if (
      !memoChangeReachesSubscribedContext(context, {
        context: changedContext,
        publicationScopes: detail.publicationScopes,
      })
    ) {
      return;
    }
    onChanged();
  };

  window.addEventListener(MEMO_CHANGED_EVENT, handleTimelineChanged);

  return () => {
    window.removeEventListener(MEMO_CHANGED_EVENT, handleTimelineChanged);
  };
}

/**
 * mutation 対象と購読対象が同じ、または広域公開 memo が同じ表示面に届く場合だけ通知します。
 * Dashboard memo は page target だけを使うため、VIEW scope でも全 card が同じ target key を購読します。
 */
function memoChangeReachesSubscribedContext(
  subscribedContext: MemoContextKeyInput,
  changedDetail: MemoChangedDetail,
) {
  if (
    memoContextKey(changedDetail.context) === memoContextKey(subscribedContext)
  ) {
    return true;
  }

  const publicationScopes = changedDetail.publicationScopes ?? ["VIEW"];
  if (
    publicationScopes.includes("WORKSPACE") &&
    changedDetail.context.workspaceId === subscribedContext.workspaceId
  ) {
    return true;
  }
  if (
    publicationScopes.includes("PROJECT") &&
    changedDetail.context.workspaceId === subscribedContext.workspaceId &&
    changedDetail.context.featureKind === subscribedContext.featureKind &&
    changedDetail.context.projectId === subscribedContext.projectId
  ) {
    return true;
  }
  return (
    publicationScopes.includes("FEATURE") &&
    changedDetail.context.workspaceId === subscribedContext.workspaceId &&
    changedDetail.context.featureKind === subscribedContext.featureKind
  );
}

/**
 * chart 上の memo 表示設定を localStorage から読みます。
 * 設定が壊れている場合は既定値に戻し、chart の描画判断が undefined に揺れないようにします。
 */
export function readMemoDisplaySettings(): MemoDisplaySettings {
  const stored = StorageManager.getJSON<unknown>(MEMO_DISPLAY_SETTINGS_KEY);
  return memoDisplaySettingsFromStored(stored) ?? DEFAULT_MEMO_DISPLAY_SETTINGS;
}

/**
 * chart 上の memo 表示設定を保存し、同じ画面内の chart / side panel へ即時通知します。
 * 永続設定と一時イベントを同時に更新して、ページ遷移後と現在表示の両方を揃えます。
 */
export function writeMemoDisplaySettings(settings: MemoDisplaySettings) {
  const serializedSettings = JSON.stringify(settings);
  cachedMemoDisplaySettings = { ...settings };
  cachedMemoDisplaySettingsRaw = serializedSettings;
  StorageManager.setItem(MEMO_DISPLAY_SETTINGS_KEY, serializedSettings);
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<MemoDisplaySettings>(MEMO_DISPLAY_SETTINGS_EVENT, {
      detail: settings,
    }),
  );
}

/**
 * chart 上の memo 表示設定変更を購読します。
 * localStorage の storage event では同一 tab 更新を拾えないため、専用 CustomEvent を使います。
 */
export function subscribeMemoDisplaySettingsChanged(
  onChanged: (settings: MemoDisplaySettings) => void,
) {
  if (typeof window === "undefined") return () => undefined;

  const handleSettingsChanged = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as unknown;
    if (!isMemoDisplaySettings(detail)) return;
    onChanged(detail);
  };

  window.addEventListener(MEMO_DISPLAY_SETTINGS_EVENT, handleSettingsChanged);

  return () => {
    window.removeEventListener(
      MEMO_DISPLAY_SETTINGS_EVENT,
      handleSettingsChanged,
    );
  };
}

/**
 * chart 上の memo 表示設定を React の external store として購読します。
 * localStorage と CustomEvent は React の外側で変化するため、useEffect ではなく
 * useSyncExternalStore に寄せて、読み取りと購読の契約を一箇所に閉じます。
 */
export function useMemoDisplaySettings() {
  return useSyncExternalStore(
    subscribeMemoDisplaySettingsStore,
    memoDisplaySettingsSnapshot,
    () => DEFAULT_MEMO_DISPLAY_SETTINGS,
  );
}

/**
 * useSyncExternalStore の getSnapshot 契約に合わせ、設定が変わらない限り同じ object 参照を返します。
 */
function memoDisplaySettingsSnapshot() {
  const rawSettings = StorageManager.getItem(MEMO_DISPLAY_SETTINGS_KEY);
  if (
    cachedMemoDisplaySettings &&
    cachedMemoDisplaySettingsRaw === rawSettings
  ) {
    return cachedMemoDisplaySettings;
  }
  cachedMemoDisplaySettingsRaw = rawSettings;
  cachedMemoDisplaySettings = readMemoDisplaySettings();
  return cachedMemoDisplaySettings;
}

/**
 * useSyncExternalStore 用に、設定変更イベントを「store が変わった」という通知へ変換します。
 */
function subscribeMemoDisplaySettingsStore(onStoreChange: () => void) {
  return subscribeMemoDisplaySettingsChanged(() => {
    onStoreChange();
  });
}

/**
 * chart が現在表示している memo 対象期間を保存し、header 起点の memo list 初期 filter と共有します。
 * URL に残す状態ではなく現在画面の補助情報なので、context key ごとの in-memory store に閉じます。
 */
export function writeMemoVisibleDateRange(
  context: MemoContextKeyInput,
  range: MemoVisibleDateRange | null,
) {
  const contextKey = memoContextKey(context);
  if (range) {
    cachedMemoVisibleDateRanges.set(contextKey, range);
  } else {
    cachedMemoVisibleDateRanges.delete(contextKey);
  }
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<{
      context: MemoContextKeyInput;
      range: MemoVisibleDateRange | null;
    }>(MEMO_VISIBLE_DATE_RANGE_EVENT, {
      detail: { context, range },
    }),
  );
}

/**
 * header / side panel が memo list の既定 date filter に使う、現在 chart 表示中の期間を読みます。
 * chart が running / empty などで期間を持たない場合は null を返し、既存どおり全期間 list に fallback します。
 */
export function readMemoVisibleDateRange(context: MemoContextKeyInput) {
  return cachedMemoVisibleDateRanges.get(memoContextKey(context)) ?? null;
}

/**
 * chart が表示中の memo 対象期間を publish し、unmount 時に同じ context の期間を消します。
 * SA / UA / IW / Dashboard で同じ cleanup 契約を使い、list の初期 filter が古い chart range を読まないようにします。
 */
export function usePublishMemoVisibleDateRange(
  context: MemoContextKeyInput | null,
  range: MemoVisibleDateRange | null,
) {
  const rangeStartDate = range?.startDate ?? null;
  const rangeEndDate = range?.endDate ?? null;

  useEffect(() => {
    if (!context) return;
    writeMemoVisibleDateRange(
      context,
      rangeStartDate && rangeEndDate
        ? { startDate: rangeStartDate, endDate: rangeEndDate }
        : null,
    );
    return () => {
      writeMemoVisibleDateRange(context, null);
    };
  }, [context, rangeEndDate, rangeStartDate]);
}

/**
 * 指定 context の visible date range 更新だけを購読します。
 * 複数 feature の header / side panel は同じ layout にいるため、feature と target を含む context key で混線を防ぎます。
 */
export function subscribeMemoVisibleDateRangeChanged(
  context: MemoContextKeyInput,
  onChanged: () => void,
) {
  if (typeof window === "undefined") return () => undefined;

  const subscribedContextKey = memoContextKey(context);
  const handleVisibleDateRangeChanged = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as {
      context?: MemoContextKeyInput;
    } | null;
    if (
      !detail?.context ||
      memoContextKey(detail.context) !== subscribedContextKey
    ) {
      return;
    }
    onChanged();
  };

  window.addEventListener(
    MEMO_VISIBLE_DATE_RANGE_EVENT,
    handleVisibleDateRangeChanged,
  );

  return () => {
    window.removeEventListener(
      MEMO_VISIBLE_DATE_RANGE_EVENT,
      handleVisibleDateRangeChanged,
    );
  };
}

/**
 * chart が publish した表示中期間を React から読む hook です。
 * header button は chart の子孫ではないため、props ではなく external store で current screen context を参照します。
 */
export function useMemoVisibleDateRange(context: MemoContextKeyInput | null) {
  return useSyncExternalStore(
    (onStoreChange) =>
      context
        ? subscribeMemoVisibleDateRangeChanged(context, onStoreChange)
        : () => undefined,
    () => (context ? readMemoVisibleDateRange(context) : null),
    () => null,
  );
}

function dispatchMemoRelatedContextsChanged(context: MemoContextKeyInput) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<{ context: MemoContextKeyInput }>(
      MEMO_RELATED_CONTEXTS_EVENT,
      { detail: { context } },
    ),
  );
}

function retainMemoRelatedContext(
  context: MemoContextKeyInput,
  relatedContext: UserMemoContext,
) {
  const contextKey = memoContextKey(context);
  const relatedContextKey = memoContextKey(relatedContext);
  const relatedContexts =
    cachedMemoRelatedContexts.get(contextKey) ?? new Map();
  const current = relatedContexts.get(relatedContextKey);
  relatedContexts.set(relatedContextKey, {
    context: relatedContext,
    count: (current?.count ?? 0) + 1,
  });
  cachedMemoRelatedContexts.set(contextKey, relatedContexts);
  cachedMemoRelatedContextSnapshots.set(
    contextKey,
    Array.from(relatedContexts.values()).map((entry) => entry.context),
  );
  dispatchMemoRelatedContextsChanged(context);
}

function releaseMemoRelatedContext(
  context: MemoContextKeyInput,
  relatedContext: UserMemoContext,
) {
  const contextKey = memoContextKey(context);
  const relatedContexts = cachedMemoRelatedContexts.get(contextKey);
  if (!relatedContexts) return;

  const relatedContextKey = memoContextKey(relatedContext);
  const current = relatedContexts.get(relatedContextKey);
  if (!current) return;

  if (current.count > 1) {
    relatedContexts.set(relatedContextKey, {
      context: current.context,
      count: current.count - 1,
    });
  } else {
    relatedContexts.delete(relatedContextKey);
  }
  if (relatedContexts.size === 0) {
    cachedMemoRelatedContexts.delete(contextKey);
    cachedMemoRelatedContextSnapshots.delete(contextKey);
  } else {
    cachedMemoRelatedContextSnapshots.set(
      contextKey,
      Array.from(relatedContexts.values()).map((entry) => entry.context),
    );
  }
  dispatchMemoRelatedContextsChanged(context);
}

/**
 * Dashboard card など page-level memo context の外側にある system memo context を共有します。
 * header の memo list button は card の子孫ではないため、visible range と同じ in-memory store に閉じます。
 */
export function usePublishMemoRelatedContext(
  context: MemoContextKeyInput | null,
  relatedContext: UserMemoContext | null,
) {
  useEffect(() => {
    if (!context || !relatedContext) return;
    retainMemoRelatedContext(context, relatedContext);
    return () => {
      releaseMemoRelatedContext(context, relatedContext);
    };
  }, [context, relatedContext]);
}

/**
 * 1 つの page-level memo context に対して、複数の system memo context をまとめて共有します。
 * Dashboard は複数 feature の chart card を持つため、カードの lazy render に依存せず page からまとめて登録します。
 */
export function usePublishMemoRelatedContexts(
  context: MemoContextKeyInput | null,
  relatedContexts: readonly UserMemoContext[],
) {
  useEffect(() => {
    if (!context || relatedContexts.length === 0) return;
    const uniqueRelatedContexts = Array.from(
      new Map(
        relatedContexts.map(
          (relatedContext) =>
            [memoContextKey(relatedContext), relatedContext] as const,
        ),
      ).values(),
    );
    for (const relatedContext of uniqueRelatedContexts) {
      retainMemoRelatedContext(context, relatedContext);
    }
    return () => {
      for (const relatedContext of uniqueRelatedContexts) {
        releaseMemoRelatedContext(context, relatedContext);
      }
    };
  }, [context, relatedContexts]);
}

export function readMemoRelatedContexts(context: MemoContextKeyInput) {
  return (
    cachedMemoRelatedContextSnapshots.get(memoContextKey(context)) ??
    EMPTY_MEMO_RELATED_CONTEXTS
  );
}

function subscribeMemoRelatedContextsChanged(
  context: MemoContextKeyInput,
  onChanged: () => void,
) {
  if (typeof window === "undefined") return () => undefined;

  const subscribedContextKey = memoContextKey(context);
  const handleRelatedContextsChanged = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as {
      context?: MemoContextKeyInput;
    } | null;
    if (
      !detail?.context ||
      memoContextKey(detail.context) !== subscribedContextKey
    ) {
      return;
    }
    onChanged();
  };

  window.addEventListener(
    MEMO_RELATED_CONTEXTS_EVENT,
    handleRelatedContextsChanged,
  );

  return () => {
    window.removeEventListener(
      MEMO_RELATED_CONTEXTS_EVENT,
      handleRelatedContextsChanged,
    );
  };
}

export function useMemoRelatedContexts(context: MemoContextKeyInput | null) {
  return useSyncExternalStore(
    (onStoreChange) =>
      context
        ? subscribeMemoRelatedContextsChanged(context, onStoreChange)
        : () => undefined,
    () =>
      context ? readMemoRelatedContexts(context) : EMPTY_MEMO_RELATED_CONTEXTS,
    () => EMPTY_MEMO_RELATED_CONTEXTS,
  );
}

/**
 * chart と side panel の hover 中 memo を同期します。
 * URL 状態に残すほど永続的な選択ではないため、view target 境界の CustomEvent だけで一時状態を共有します。
 */
export function dispatchMemoHovered(detail: MemoHoverDetail) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<MemoHoverDetail>(MEMO_HOVERED_EVENT, { detail }),
  );
}

/**
 * 指定 view の hover 同期だけを購読します。
 * chart と side panel の双方向連動で、他 feature / target の hover が混ざらないよう context key を検証します。
 */
export function subscribeMemoHovered(
  context: MemoContextKeyInput,
  onHovered: (detail: MemoHoverDetail) => void,
) {
  if (typeof window === "undefined") return () => undefined;

  const handleTimelineHovered = (event: Event) => {
    if (!(event instanceof CustomEvent)) return;
    const detail = event.detail as Partial<MemoHoverDetail> | null;
    if (
      !detail?.context ||
      !memoHoverReachesSubscribedContext(context, detail) ||
      !detail.source
    ) {
      return;
    }
    onHovered({
      context: detail.context,
      targetId:
        typeof detail.targetId === "string"
          ? detail.targetId
          : detail.context.targetId,
      source: detail.source,
      memoIds: Array.isArray(detail.memoIds) ? detail.memoIds : [],
      bucket: detail.bucket ?? null,
      startDate: detail.startDate ?? null,
      endDate: detail.endDate ?? null,
    });
  };

  window.addEventListener(MEMO_HOVERED_EVENT, handleTimelineHovered);

  return () => {
    window.removeEventListener(MEMO_HOVERED_EVENT, handleTimelineHovered);
  };
}

/**
 * hover event が購読中 chart に届くかを判定します。
 * Dashboard も page target だけを使うため、memo list と各 card は同じ context key で同期します。
 */
function memoHoverReachesSubscribedContext(
  subscribedContext: MemoContextKeyInput,
  detail: Partial<MemoHoverDetail>,
) {
  return Boolean(
    detail.context &&
      memoContextKey(detail.context) === memoContextKey(subscribedContext),
  );
}
